import { Box, CircularProgress, Tooltip } from '@mui/material'
import { InventorySummary } from 'Features/Projects/Scenarios/Inventory/Inventory.types'
import {
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import { ReactNode, useMemo, useState } from 'react'
import { ActionItem } from 'Common/Components/MUITableAction'
import {
  InventoryPayloadType,
  useCalculateSafetyStock,
} from 'Features/Projects/Scenarios/Inventory/Inventory.data'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from 'Common/Supplyvue-ui'
import { Table } from 'Common/MUITableV2'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Status } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'

export const InventorySummaryTable = ({
  data,
  isRefetching,
  handleRefetch,
}: {
  data: InventorySummary[]
  isRefetching: boolean
  handleRefetch: () => void
}) => {
  const { projectId, scenarioId } = useParams<{
    projectId: string
    scenarioId: string
  }>() as { projectId: string; scenarioId: string }

  const navigate = useNavigate()

  const { mutate: calculateSafetyStock, isLoading: isCalculating } =
    useCalculateSafetyStock(
      () => {
        toast.success('Success')
      },
      () => {
        toast.error('Error')
      }
    )

  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([])
  const handleRowSelectionChange = (
    rowSelectionModel: GridRowSelectionModel
  ) => {
    setSelectedRows([...rowSelectionModel.ids])
  }

  const handleCalculateSafetyStock = (selectedRows: GridRowId[]) => {
    const payload: InventoryPayloadType[] = data
      .filter((setting: InventorySummary) => selectedRows.includes(setting.id))
      .map((setting: InventorySummary) => ({
        siteKey: setting.siteKey,
      }))
    calculateSafetyStock({ projectId, scenarioId, payload })
  }

  const getStatusIcon = (status: Status) => {
    let content: ReactNode

    switch (status) {
      case 'completed':
        content = (
          <Tooltip title="Completed">
            <CheckCircleIcon color="primary" />
          </Tooltip>
        )
        break
      case 'unstarted':
        content = <RadioButtonUncheckedIcon color="disabled" />
        break
      case 'initiated':
        content = (
          <Tooltip title="Processing">
            <CircularProgress color="primary" size={20} />
          </Tooltip>
        )
        break
      case 'processing':
        content = (
          <Tooltip title="Processing">
            <CircularProgress color="primary" size={20} />
          </Tooltip>
        )
        break
      case 'failed':
        content = (
          <Tooltip title="Failed">
            <CancelOutlinedIcon color="error" />
          </Tooltip>
        )
        break
      default:
        content = <RadioButtonUncheckedIcon color="disabled" />
    }

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        {content}
      </Box>
    )
  }

  const COLUMN_WIDTH = 150

  const handleViewResults = (params: GridRowParams<InventorySummary>): void => {
    const { siteKey } = params.row
    navigate(
      `/projects/${projectId}/scenarios/${scenarioId}/scenario/inventory-results/${siteKey}`
    )
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'siteCode',
        headerName: 'Site',
        type: 'string',
        width: COLUMN_WIDTH,
        flex: 1,
      },
      {
        field: 'skuGroupCount',
        headerName: 'Product Count',
        type: 'number',
        width: COLUMN_WIDTH,
        flex: 1,
      },
      {
        field: 'safetyStockStatus',
        headerName: 'Status',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<any, any, any>) => {
          return getStatusIcon(params.value)
        },
        type: 'string',
        width: 125,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        getActions: (params: GridRowParams<InventorySummary>) => {
          const { safetyStockStatus } = params.row
          return [
            <ActionItem
              key="view"
              showInMenu
              label="Results"
              disabled={safetyStockStatus !== 'completed'}
              clickHandler={() => {
                handleViewResults(params)
              }}
            />,
          ]
        },
      },
    ],
    [data]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="text"
          sx={{ fontWeight: 500 }}
          startIcon={<RefreshIcon />}
          onClick={handleRefetch}
        >
          Refresh
        </Button>

        <Button
          variant="outlined"
          disabled={selectedRows.length === 0 || isCalculating}
          size="small"
          onClick={() => handleCalculateSafetyStock(selectedRows)}
        >
          {isCalculating && (
            <CircularProgress
              color="inherit"
              size={16}
              sx={{
                marginRight: '8px',
              }}
            />
          )}
          Calculate Safety Stock
        </Button>
      </Box>
      <Table
        rows={data}
        columns={columns}
        checkboxSelection={true}
        onRowSelectionModelChange={handleRowSelectionChange}
        showCellVerticalBorder
        showColumnVerticalBorder
        loading={isRefetching}
      />
    </Box>
  )
}
